import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateGameplan,
  useGameplanList,
  type CampaignListItem,
  type CampaignType,
} from "../../hooks/useGameplans";
import { usePersona } from "../../hooks/usePersona";

const TYPE_LABELS: Record<CampaignType, string> = {
  strategy: "Strategy",
  security_incident: "Security",
  it_ops: "IT Ops",
  hr: "HR",
  finance: "Finance",
  generic: "General",
};

export default function GamePlansIndex() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const persona = usePersona(workspaceId);
  const canCreate = persona === "owner" || persona === "cxo";

  const [tab, setTab] = useState<"active" | "closed">("active");
  const [showCreate, setShowCreate] = useState(false);
  const q = useGameplanList(workspaceId, tab);

  return (
    <div className="main-inner" data-testid="gameplans-index">
      <div
        className="page-h"
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          marginBottom: 4,
        }}
      >
        <span
          className="ttl"
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "-0.018em",
            color: "var(--text)",
          }}
        >
          GamePlans
        </span>
        <span style={{ fontSize: 12.5, color: "var(--text-3)", flex: 1 }}>
          {q.data?.items.length ?? 0} {tab === "closed" ? "closed" : "active"}
        </span>
        {canCreate ? (
          <button
            type="button"
            className="btn primary"
            onClick={() => setShowCreate(true)}
            data-testid="gameplans-new"
          >
            + New GamePlan
          </button>
        ) : null}
      </div>
      <p className="page-sub">
        Group related plays under a shared execution context. Use a GamePlan
        when several plays share a common outcome — a transformation program,
        a security incident, a mandate rebalance — so their progress, context,
        and decisions live in one place instead of scattered across plays.
      </p>

      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {(["active", "closed"] as const).map((k) => (
          <button
            type="button"
            key={k}
            className={`btn ${tab === k ? "primary" : ""}`}
            onClick={() => setTab(k)}
            data-testid={`gameplans-tab-${k}`}
            style={{ textTransform: "capitalize" }}
          >
            {k}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <div style={{ color: "var(--text-3)" }}>Loading…</div>
      ) : q.error ? (
        <div style={{ color: "var(--danger)" }}>
          Failed to load: {(q.error as Error).message}
        </div>
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          tab={tab}
          canCreate={canCreate}
          onCreate={() => setShowCreate(true)}
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          {q.data!.items.map((c) => (
            <GameplanCard
              key={c.id}
              c={c}
              onOpen={() =>
                navigate(`/workspaces/${workspaceId}/gameplans/${c.id}`)
              }
            />
          ))}
        </div>
      )}

      {showCreate ? (
        <CreateModal
          workspaceId={workspaceId}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/workspaces/${workspaceId}/gameplans/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function EmptyState({
  tab,
  canCreate,
  onCreate,
}: {
  tab: "active" | "closed";
  canCreate: boolean;
  onCreate: () => void;
}) {
  if (tab === "closed") {
    return (
      <div
        style={{
          border: "1px dashed var(--border)",
          borderRadius: 8,
          padding: 40,
          textAlign: "center",
          color: "var(--text-3)",
          fontSize: 13,
        }}
        data-testid="gameplans-empty"
      >
        No closed GamePlans yet. GamePlans you close (resolving → closed) will
        appear here for historical reference.
      </div>
    );
  }
  return (
    <div
      style={{
        border: "1px dashed var(--border)",
        borderRadius: 10,
        padding: 32,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        alignItems: "center",
        textAlign: "center",
        color: "var(--text-2)",
      }}
      data-testid="gameplans-empty"
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: "var(--text)",
        }}
      >
        No active GamePlans yet.
      </div>
      <div
        style={{
          fontSize: 13,
          color: "var(--text-2)",
          maxWidth: 540,
          lineHeight: 1.55,
        }}
      >
        A GamePlan groups related plays under one shared context. The shared
        context is a fact ledger — findings from one play become inputs to the
        next — so a transformation program, an incident, or a mandate
        rebalance reads as a single coherent campaign instead of scattered
        plays.
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          maxWidth: 640,
          width: "100%",
          marginTop: 6,
        }}
      >
        <HowToCard
          title="Start from scratch"
          body="Create a GamePlan first, then attach existing plays or spawn new ones into it."
          action={canCreate ? "+ New GamePlan" : null}
          onClick={canCreate ? onCreate : undefined}
        />
        <HowToCard
          title="Split an inbox initiative"
          body="In the Inbox, click ‘Convert to GamePlan’ on a complex initiative — it splits into child entries that all share one GamePlan."
        />
      </div>
    </div>
  );
}

function HowToCard({
  title,
  body,
  action,
  onClick,
}: {
  title: string;
  body: string;
  action?: string | null;
  onClick?: (() => void) | undefined;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: 14,
        background: "var(--bg-soft)",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>
        {title}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
        {body}
      </div>
      {action ? (
        <button
          type="button"
          className="btn primary"
          onClick={onClick}
          style={{ alignSelf: "flex-start", marginTop: 4, fontSize: 12 }}
        >
          {action}
        </button>
      ) : null}
    </div>
  );
}

function GameplanCard({
  c,
  onOpen,
}: {
  c: CampaignListItem;
  onOpen: () => void;
}) {
  return (
    <div
      onClick={onOpen}
      data-testid={`gameplan-card-${c.id}`}
      style={{
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: 14,
        background: "var(--bg)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        transition: "border-color .1s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
    >
      <div
        style={{
          fontSize: 10,
          color: "var(--text-3)",
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}
      >
        {TYPE_LABELS[c.type] ?? c.type}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{c.title}</div>
      {c.description ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-2)",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {c.description}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: "auto",
          alignItems: "center",
        }}
      >
        <StatusPill status={c.status} />
        {c.severity ? <SeverityPill severity={c.severity} /> : null}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "var(--text-3)" }}>
          {c.playCount} {c.playCount === 1 ? "play" : "plays"}
        </span>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: CampaignListItem["status"] }) {
  const map = {
    active: { bg: "#dcfce7", fg: "#166534" },
    escalated: { bg: "#fef3c7", fg: "#854d0e" },
    resolving: { bg: "#dbeafe", fg: "#1e40af" },
    closed: { bg: "#f1f5f9", fg: "#475569" },
  }[status];
  return (
    <span
      style={{
        fontSize: 10,
        padding: "2px 8px",
        borderRadius: 4,
        background: map.bg,
        color: map.fg,
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );
}

function SeverityPill({
  severity,
}: {
  severity: NonNullable<CampaignListItem["severity"]>;
}) {
  const map = {
    low: { bg: "#f1f5f9", fg: "#475569" },
    medium: { bg: "#dbeafe", fg: "#1e40af" },
    high: { bg: "#fef3c7", fg: "#854d0e" },
    critical: { bg: "#fee2e2", fg: "#991b1b" },
  }[severity];
  return (
    <span
      style={{
        fontSize: 10,
        padding: "2px 8px",
        borderRadius: 4,
        background: map.bg,
        color: map.fg,
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    >
      {severity}
    </span>
  );
}

function CreateModal({
  workspaceId,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState<CampaignType>("generic");
  const [description, setDescription] = useState("");
  const m = useCreateGameplan(workspaceId);

  return (
    <div
      role="dialog"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          borderRadius: 10,
          width: 460,
          maxWidth: "90vw",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
        data-testid="gameplans-create-modal"
      >
        <h2 style={{ margin: 0 }}>New GamePlan</h2>
        <label style={{ fontSize: 12, color: "var(--text-2)" }}>
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="gameplans-create-title"
            style={{
              display: "block",
              width: "100%",
              padding: 8,
              marginTop: 4,
              border: "1px solid var(--border)",
              borderRadius: 4,
            }}
          />
        </label>
        <label style={{ fontSize: 12, color: "var(--text-2)" }}>
          Type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as CampaignType)}
            data-testid="gameplans-create-type"
            style={{
              display: "block",
              width: "100%",
              padding: 8,
              marginTop: 4,
            }}
          >
            {(Object.keys(TYPE_LABELS) as CampaignType[]).map((k) => (
              <option key={k} value={k}>
                {TYPE_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12, color: "var(--text-2)" }}>
          Description (optional)
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="gameplans-create-description"
            style={{
              display: "block",
              width: "100%",
              padding: 8,
              marginTop: 4,
              border: "1px solid var(--border)",
              borderRadius: 4,
              fontFamily: "inherit",
            }}
          />
        </label>
        {m.error ? (
          <div style={{ color: "var(--danger)", fontSize: 12 }}>
            {(m.error as Error).message}
          </div>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 8,
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={m.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="gameplans-create-submit"
            disabled={!title.trim() || m.isPending}
            onClick={() =>
              m.mutate(
                {
                  title: title.trim(),
                  type,
                  ...(description.trim() && { description: description.trim() }),
                },
                { onSuccess: (c) => onCreated(c.id) },
              )
            }
          >
            {m.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
